import { Permissions } from "@cnx/common";
import { useMediaQuery } from "@mantine/hooks";
import {
  IconBuildingWarehouse,
  IconShieldLock,
  IconUsers,
} from "@tabler/icons-react";
import { FEATURE_TERMINAL_MANAGEMENT } from "common/constants";
import theme from "common/theme";
import { useFeatureSupport } from "hooks/useFeatureSupport";
import IconUserGroupUseRights from "icons/users/userGroupUseRights.svg";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import { useAuthStore } from "stores/authStore";
import { Link } from "types/Link";

const USE_RIGHTS_TABLE_PATH = "/roles/use-rights";

export const useBasicInfoLinks = () => {
  const useRights = new Set(useAuthStore(state => state.useRights));
  const { t } = useTranslation();
  const { isFeatureEnabled } = useFeatureSupport();
  const { pathname: currentPath } = useLocation();
  const isDesktop = useMediaQuery(
    `(min-width: ${theme.breakpoints?.lg})`,
    undefined,
    { getInitialValueInEffect: false }
  );
  const isUseRightsTablePath = currentPath.startsWith(USE_RIGHTS_TABLE_PATH);

  const basicInfoLinks: Link[] = [];

  const hasRolesRight =
    useRights.has(Permissions.RIGHTS_ROLES_USER) ||
    useRights.has(Permissions.RIGHTS_ROLES_ADMIN);

  hasRolesRight &&
    basicInfoLinks.push({
      isActive: currentPath.startsWith("/roles") && !isUseRightsTablePath,
      link: "/roles",
      icon: <IconUsers />,
      label: t("roles"),
    });
  hasRolesRight &&
    isDesktop &&
    basicInfoLinks.push({
      isActive: isUseRightsTablePath,
      link: USE_RIGHTS_TABLE_PATH,
      icon: <IconUserGroupUseRights className="w-6 h-6" />,
      label: t("useRightsTable"),
    });
  useRights.has(Permissions.RIGHTS_USE_RIGHTS_ADMIN) &&
    basicInfoLinks.push({
      isActive: currentPath.startsWith("/features"),
      link: "/features",
      icon: <IconShieldLock />,
      label: t("rights"),
    });
  useRights.has(Permissions.RIGHTS_STAKEHOLDERS_ADMIN) &&
    basicInfoLinks.push({
      isActive: currentPath === "/stakeholders",
      link: "/stakeholders",
      icon: <IconUsers />,
      label: t("stakeholders"),
    });
  useRights.has(Permissions.RIGHTS_TERMINAL_ADMIN) &&
    isFeatureEnabled(FEATURE_TERMINAL_MANAGEMENT) &&
    basicInfoLinks.push({
      isActive: currentPath.startsWith("/terminals"),
      link: "/terminals",
      icon: <IconBuildingWarehouse className="w-6 h-6" />,
      label: t("terminals"),
    });

  return basicInfoLinks;
};
